
import { StateServices } from '../instance-service/i-state-services';
import { Vector3 } from 'three/src/math/Vector3.js';
import { RenderServices } from '../instance-service/i-render-services';
import { Entity } from 'src/app/state/entities/entity.model';
import { Surface } from '../../surface/primitives/surface';
import { Container } from 'pixi.js';
import { CameraState } from 'src/app/state/camera/camera.state';
import { entityActions } from 'src/app/state/entities/entity.actions';
import { Vector2 } from 'three';
import { degToRad } from 'three/src/math/MathUtils.js';
import { PointerTracker } from '../../inputs/primitives/pointer-tracker';
import { Rect } from '../../primitives/rect';
import { Blueprint, InitType } from './blueprint';
import {BlueprintContext} from "./i-blueprint-context";
import moment from "moment";
import {animate, JSAnimation, utils} from 'animejs';
import {Subscription} from "rxjs";
import {MemoizedSelector} from "@ngrx/store";
import {PixiObject} from "../../rendering/pixi/primitives/pixi-object";
import {TextDrawable} from "../../rendering/pixi/drawables/text.pixi";
import {emojiTextStyle} from "./map-reaction/map-reaction";
import {DebugRectPixi} from "../../rendering/pixi/drawables/debug-rect.pixi";

export abstract class BlueprintSurface extends Blueprint{
  entity: Entity;
  surface: Surface;
  container: Container;
  isMoving = false;
  camera: CameraState;
  unsetMovingDebouncerTimeout: NodeJS.Timeout = null;

  blueprintContext: BlueprintContext
  stateServices: StateServices
  renderServices: RenderServices
  subscriptions: Subscription[] = [];
  components: Map<string, PixiObject> = new Map();

  debug = false

  rotateAnim: JSAnimation

  override async init(config: {
    initType: InitType,
  } & BlueprintContext) {
    this.id = config.entity.id
    this.entity = config.entity;
    this.id = config.entity.id;
    this.stateServices = config.stateServices
    this.renderServices = config.renderServices
    this.blueprintContext = config
  }

  log(text: string) {
    if (this.debug) {
      console.log(`${moment().format('h:mm:ss.SSS A')} ${this.id?.slice(0,5)}: ${text}`)
    }
  }

  subscribe<Result>(
      selector: (state: any) => Result,
      callback: (param: Result) => void | Promise<void>,
  ) {
    this.subscriptions.push(
        this.blueprintContext.stateServices.store
            .select(selector)
            .subscribe(async (item: Result) => {
              if (!item) return
              if (callback instanceof Promise) {
                await callback(item)
              }
              callback(item)
            })
    )
  }

  async setComponent(id: string, createComponent: () => PixiObject) {
    if (!Object.keys(this.components).includes(id)) {
      this.components.set(id, createComponent())
      if (this.surface) {
        this.surface.addPixiPrimitive(this.components.get(id))
      }
    }
  }

  allComponentsExist(componentIds: string[]) {
    if (componentIds.every(id => this.components.has(id))) {
      return true
    }
    return false
  }

  componentExists(id: string) {
    return this.components.has(id)
  }

  getComponent(id: string) {
    return this.components.get(id)
  }

  setupSurfaceMovementHandlers(surface: Surface, isOwner: boolean) {
    if (isOwner) {
      this.surface.rect.onMove(this.move.bind(this));
    }
    this.surface.rect.onHover(this.hover.bind(this));
    this.surface.rect.onHoverNear(this.hoverNear.bind(this));
    this.surface.mesh.position.x = this.entity.coord.x;
    this.surface.mesh.position.y = this.entity.coord.y;
    this.surface.mesh.position.z = this.entity.coord.z;
  }

  abstract makeSurface(config: {
    entity: Entity, 
    initType: InitType, 
    stateServices: StateServices,
    renderServices: RenderServices
  }): Promise<Surface>

  override updatePosition(config: { newPos: Vector3; }) {
    if (!this.isMoving) {
      this.surface.mesh.position.x = config.newPos.x;
      this.surface.mesh.position.y = config.newPos.y;
    }
  }

  move(pos: Vector2) {
    const moveDist = this.renderServices.threeService.pixelToObjectDistance(
      pos.multiplyScalar(1), this.surface.mesh.position.z
    );
    const move = new Vector3(-moveDist.x, moveDist.y, 0);
    this.surface.x -= move.x;
    this.surface.y -= move.y;
    // this.surface.mesh.position.sub(move);
    const pos2d = new Vector2(
      this.surface.mesh.position.x,
      this.surface.mesh.position.y,
    );
    this.stateServices.websocketService.dispatch(
      entityActions.netMoved({
        entityId: this.id,
        oldPos: this.surface.mesh.position,
        newPos: new Vector3(pos2d.x, pos2d.y, 5),
      }),
    );
    this.isMoving = true;
    // clearTimeout(this.unsetMovingDebouncerTimeout);
    // this.unsetMovingDebouncerTimeout = setTimeout(
    //   (() => {
    //     this.isMoving = false;
    //   }).bind(this),
    //   1000,
    // );
    return false;
  }

  hover(pointer: PointerTracker) {
    this.log(pointer.relPos.x + " " + pointer.relPos.y)
    let modx = -0.5 + pointer.relPos.y / this.surface.rect.height;
    let mody = -0.5 + pointer.relPos.x / this.surface.rect.width;
    // const insideRect = Rect.FromUnitRect(
    //   this.surface.rect,
    //   new Rect({
    //     left: 3 * (1 - Math.abs(modx)),
    //     top: 4 * (1 - Math.abs(mody)),
    //     right: 100 - 3 * (1 - Math.abs(modx)),
    //     bottom: 100 - 4 * (1 - Math.abs(mody)),
    //   }),
    // );
    // if (insideRect.intersectsPoint(pointer.relPos)) {
    //   this.clearAnim()
      // this.rotateAnim = animate(this.surface.mesh.rotation, {
      //   x: degToRad(modx) * 10,
      //   y: degToRad(mody) * 10,
      //   duration: 100,
      // });
      // this.surface.mesh.rotation.y = degToRad(mody) * 40;
      // this.surface.mesh.rotation.x = degToRad(modx) * 40;
      // this.surface.mesh.rotation.y = 0;
      // this.surface.mesh.rotation.x = 0;
    // }
    return false;
  }

  clearAnim() {
    if (this.rotateAnim) {
      this.rotateAnim.pause();
      this.rotateAnim = null;
    }
  }

  hoverNear() {
    // this.clearAnim()
    // this.rotateAnim = animate(this.surface.mesh.rotation, {
    //   x: 0,
    //   y: 0,
    //   duration: 100,
    //   ease: 'easeOut(3)'
    // });
  }

  setupBackgroundDebugRect() {
    const drp = new DebugRectPixi({
      left: 0,
      top: 0,
      right: this.surface.rect.width,
      bottom: this.surface.rect.height,
      roundness: 10,
      colour: [Math.random(), Math.random(), Math.random(), 0.5],
    });
    this.surface.stage.addChild(drp.sprite);
    drp.sprite.zIndex = 100;
  }
}
